import { BinaryHeaderInfo } from './types';

const MAX_APP_SIZE = 90 * 1024; // 92,160 bytes (45 pages * 2048)
const SRAM_BASE = 0x20000000;
const SRAM_END = 0x2000A000; // 40KB STM32U073 SRAM
const APP_FLASH_BASE = 0x08005000;
const APP_FLASH_END = 0x0801F000;

export function inspectBinary(buffer: ArrayBuffer): BinaryHeaderInfo {
  const bytes = new Uint8Array(buffer);
  const sizeBytes = bytes.byteLength;

  if (sizeBytes < 512) {
    return {
      valid: false,
      modelDetected: 'UNKNOWN',
      initialSP: 0,
      resetHandler: 0,
      sizeBytes,
      error: 'File is too small to be a valid STM32 application binary (< 512 bytes).'
    };
  }

  if (sizeBytes > MAX_APP_SIZE) {
    return {
      valid: false,
      modelDetected: 'UNKNOWN',
      initialSP: 0,
      resetHandler: 0,
      sizeBytes,
      error: `File size (${(sizeBytes / 1024).toFixed(1)} KB) exceeds the 90 KB DFU flash staging limit!`
    };
  }

  const view = new DataView(buffer);
  const initialSP = view.getUint32(0, true);
  const resetHandler = view.getUint32(4, true);

  // Validate SP within Cortex-M0+ SRAM
  if (initialSP < SRAM_BASE || initialSP > SRAM_END + 0x400) {
    return {
      valid: false,
      modelDetected: 'UNKNOWN',
      initialSP,
      resetHandler,
      sizeBytes,
      error: `Invalid initial Stack Pointer: 0x${initialSP.toString(16).toUpperCase()} (Expected in SRAM range 0x20000000 - 0x2000A000).`
    };
  }

  // Validate Reset Handler pointing into 0x08005000+ (Thumb mode bit 0 should be 1)
  if (resetHandler < APP_FLASH_BASE || resetHandler > APP_FLASH_END) {
    return {
      valid: false,
      modelDetected: 'UNKNOWN',
      initialSP,
      resetHandler,
      sizeBytes,
      error: `Invalid Reset Handler: 0x${resetHandler.toString(16).toUpperCase()} (Expected in application flash 0x08005000 - 0x0801EFFF).`
    };
  }

  // Scan ASCII content for model & version identifiers
  let ascii = '';
  const scanLimit = Math.min(sizeBytes, 32768);
  for (let i = 0; i < scanLimit; i++) {
    const c = bytes[i];
    if (c >= 32 && c <= 126) {
      ascii += String.fromCharCode(c);
    } else {
      ascii += ' ';
    }
  }

  let modelDetected: 'FP102' | 'FV801' | 'UNKNOWN' = 'UNKNOWN';
  if (ascii.includes('FV801') || ascii.includes('fv801')) {
    modelDetected = 'FV801';
  } else if (ascii.includes('FP102') || ascii.includes('fp102')) {
    modelDetected = 'FP102';
  }

  // Look for version patterns like V1.1.006, V1.1.007, v2.0.000
  const verMatch = ascii.match(/V\d+\.\d+\.\d+/i);
  const versionGuess = verMatch ? verMatch[0] : undefined;

  return {
    valid: true,
    modelDetected,
    initialSP,
    resetHandler,
    sizeBytes,
    versionGuess
  };
}
